import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter, map, switchMap, take } from 'rxjs';

import { selectIsCompanyAccessSuspended } from '@ninety/platform/state/app-global/company/company-state.selectors';
import { selectHasTier1Data } from '@ninety/platform/state/app-init/app-init.selectors';

export const ACCESS_SUSPENDED_PATH = 'access-suspended';

/**
 * Redirects to the suspension notice route when the current company has `settings.accessSuspended`
 * set. Bypasses itself for navigations already targeting the notice route so the redirect can't loop.
 *
 * Waits for tier 1 data before reading company state: sibling canActivate guards on the same route
 * run concurrently rather than sequentially, so without this a `take(1)` here can snapshot the
 * company slice before `FullAppInit` has hydrated it, always reading `accessSuspended` as undefined.
 */
export const companyAccessSuspendedGuard: CanActivateFn = (_route, state) => {
  if (state.url.startsWith(`/${ACCESS_SUSPENDED_PATH}`)) return true;

  const store = inject(Store);
  const router = inject(Router);

  return store.select(selectHasTier1Data).pipe(
    filter(Boolean),
    switchMap(() => store.select(selectIsCompanyAccessSuspended)),
    take(1),
    map(isSuspended => !isSuspended || router.createUrlTree([`/${ACCESS_SUSPENDED_PATH}`]))
  );
};
